import { useEffect, useMemo, useState, useCallback, useRef } from "react";

import _ from "lodash";

import { TabPanel, Tabs, PixelPlaceholderLoader, CircleSwitcherItem, Switcher } from "@similarweb/ui-components";

import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import Chart from "pro-features/components/Chart/src/Chart";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import markerWithDashedConfig from "pro-features/components/Chart/src/configs/series/markerWithDashedLinePerPointChartConfig";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import {
    ContentContainer,
    TabListStyled,
    TabsContainer,
} from "pro-features/components/Rule/src/RuleModes/EditRule/EditRuleModeStyles";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import {
    dateToUTC,
    getChartConfig,
} from "pro-features/pages/segments/components/benchmarkOvertime/SegmentsVsGroupLineChart";
import { EDisplayType, displayTypeItems } from "app/pages/segments/mmx/displayTypeItems";
import { SegmentChannelsLegends } from "app/pages/segments/mmx/SegmentChannelsLegends";
import { SegmentsMmxChannelsMapping } from "app/pages/segments/mmx/SegmentsMarketingChannelsConfig";
import type { ISegmentsMMXGraphData } from "app/pages/segments/mmx/SegmentsSingleMarketingChannelsContainer";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import {
    ButtonsContainer,
    LegendsTitle,
    StyledTabIcon,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/StyledComponents";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { PdfExportService } from "app/services/PdfExportService";
import type { IBaseSingleRequestParams } from "app/services/segments/segmentsApiService";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import { allTrackers } from "app/services/track/track";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn, FlexRow, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { devicesTypes } from "../../../UtilitiesAndConstants/Constants/DevicesTypes";

import type { IEngagementMetricVertical } from "./EngagementVerticals";
import { EngagementVerticals } from "./EngagementVerticals";
import { getAvailableGranularities } from "./MMXCommon";
import { SegmentsSingleGraphLegendsContext } from "./SegmentsSingleGraphLegendsContext";
import {
    BoxContainer,
    ChartContainer,
    FlexColumnContainer,
    NoDataGraphContainer,
    SectionContainer,
    SectionLine,
    SingleGraphContainer,
    SingleGraphLegendContainer,
    StyledHeaderTitle,
    StyledTab,
    StyledTitleContainer,
    SwitcherContainer,
    TotalChartContainer,
} from "./styledComponents";

const MIN_VALUE_TO_SHOW_DATA = 5000;

interface ISegmentsSingleMarketingChannelsGraphChartProps {
    params: any;
    data: ISegmentsMMXGraphData;
    isLoading: boolean;
    segmentData: any;
}

export const SegmentsSingleMarketingChannelsGraphChart = (props: ISegmentsSingleMarketingChannelsGraphChartProps) => {
    const { i18n } = useMemo(
        () => ({
            i18n: i18nFilter(),
        }),
        [],
    );

    const { params, data, isLoading, segmentData } = props;
    const { id, duration, comparedDuration, country, webSource } = params;

    const { segmentsApiService } = useMemo(
        () => ({
            segmentsApiService: new SegmentsApiService(),
        }),
        [],
    );

    const apiParams = proNavigator.getApiParams(params);
    const durationObject = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [duration, comparedDuration],
    );

    const availableGranularities = useMemo(
        () => getAvailableGranularities(durationObject, webSource),
        [duration, webSource],
    );
    useEffect(() => {
        const durationRaw = durationObject?.raw;
        const monthDiff = durationRaw.to.diff(durationRaw.from, "month");
        if (webSource !== devicesTypes.TOTAL && monthDiff < 1 && selectedGranularity.value === "Monthly") {
            setSelectedGranularity(availableGranularities[1]);
        }
    }, [duration, webSource]);
    const [selectedGranularity, setSelectedGranularity] = useState(availableGranularities[2]);

    const excelUrl: string = useMemo(() => {
        const excelFileName = `SegmentMarketingChannels - ${segmentData.segmentName} - (${params.country}) - (${durationObject.forAPI.from}) - (${durationObject.forAPI.to})`;
        return segmentsApiService.getCustomSegmentMarketingMixGraphExcelUrl({
            ...apiParams,
            id: undefined,
            segmentsIds: params.id,
            keys: "null",
            webSource: params.webSource,
            timeGranularity: selectedGranularity?.value,
            includeSubDomains: true,
            lastUpdated: segmentData.lastUpdated,
            FileName: excelFileName,
        } as IBaseSingleRequestParams);
    }, [params, selectedGranularity]);

    const subtitleFilters = useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: durationObject.raw.from.valueOf(),
                    to: durationObject.raw.to.valueOf(),
                    useRangeDisplay:
                        duration !== "28d" &&
                        durationObject.raw.from.format("YYYY-MM") !== durationObject.raw.to.format("YYYY-MM"),
                },
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
            },
            {
                filter: "webSource",
                value: params.webSource,
            },
        ],
        [durationObject, country, webSource, duration],
    );

    const [selectedDisplayTypeIndex, setSelectedDisplayTypeIndex] = useState(0);
    const [selectedTab, setSelectedTab] = useState(0);
    const [hiddenLegends, setHiddenLegends] = useState([]);
    const toggleGranularity = (index) => {
        const selectedGranularity = availableGranularities[index];
        setSelectedGranularity(selectedGranularity);
        allTrackers.dispatchTrackEvent("Time Frame Button", "switch", "Traffic and engagement", {
            Granularity: selectedGranularity.value,
        });
    };
    const getTabProps = (index, isLocked?) => {
        return {
            onClick: () => onTabSelect(index),
            selected: selectedTab === index,
            key: `tab-${index}`,
        };
    };

    const toggleSelectedDisplay = (index) => {
        setSelectedDisplayTypeIndex(index);
        allTrackers.dispatchTrackEvent("Measure Button", "click", "Traffic and engagement", {
            Value: index === EDisplayType.PERCENTAGE ? "percentage" : "number",
        });
    };

    const onTabSelect = (index: number) => {
        setSelectedTab(index);
        allTrackers.dispatchTrackEvent("Metric Button", "switch", "Traffic and engagement", {
            Metric: Object.keys(EngagementVerticals)[index],
        });
    };

    const chartRef = useRef<HTMLDivElement>(null);
    const getPNG = useCallback((): Promise<void> => {
        const metric = Object.keys(EngagementVerticals)[selectedTab];
        allTrackers.dispatchTrackEvent(
            "segments.mmx.single.graph.download.png",
            "submit-ok",
            "segments.mmx.single.graph.download.png",
        );
        const offSetX = 0;
        const offSetY = 50;
        const styleHTML = PdfExportService.getStylesheetsForPng();
        return PdfExportService.downloadHtmlPngFedService(
            styleHTML + chartRef.current.outerHTML,
            "SegmentsMmxSingleGraph",
            chartRef.current.offsetWidth + offSetX,
            chartRef.current.offsetHeight + offSetY,
        ).then();
    }, []);

    const onExcelClick = () => {
        allTrackers.dispatchTrackEvent(
            "segments.mmx.single.graph.download.excel",
            "submit-ok",
            "segments.mmx.single.graph.download.excel",
        );
    };

    const getUtilityButtons = () => {
        return (
            <ButtonsContainer>
                {excelUrl ? (
                    <DownloadButtonMenu
                        Excel={true}
                        downloadUrl={excelUrl}
                        exportFunction={onExcelClick}
                    />
                ) : null}
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={getPNG}
                />
            </ButtonsContainer>
        );
    };
    const selectedMetric = Object.keys(EngagementVerticals)[selectedTab];
    const isPercentage =
        selectedMetric === EngagementVerticals.Visits.name && selectedDisplayTypeIndex === EDisplayType.PERCENTAGE;
    const transformedData = useMemo(() => {
        if (isLoading || !data) {
            return null;
        }
        const filteredChartData = Object.entries(
            data?.[params.id]?.[selectedGranularity.value]?.[selectedMetric],
        )?.reduce((acc, channel) => {
            if (!hiddenLegends.includes(channel[0])) {
                acc[channel[0]] = channel[1];
            }
            return acc;
        }, {});
        const filteredChannelsMapping = SegmentsMmxChannelsMapping.filter(
            ({ dataKey }) => !hiddenLegends.includes(dataKey),
        );
        return transformData(filteredChartData, filteredChannelsMapping, isPercentage);
    }, [selectedGranularity, selectedDisplayTypeIndex, selectedMetric, isLoading, hiddenLegends]);

    const isEmptyData = useMemo(() => {
        if (isLoading) {
            return false;
        }
        if (!data) {
            return true;
        } else {
            const visitsData = data?.[params.id]?.Monthly?.[EngagementVerticals.Visits.dataKey];
            const totalTrafficValue: any = Object.values(visitsData)?.reduce((acc, { Total }) => acc + Total?.Value, 0);
            return totalTrafficValue < MIN_VALUE_TO_SHOW_DATA;
        }
    }, [isLoading]);

    const tabsContainerContent = useMemo(() => {
        return (
            <TabsContainer>
                <TabListStyled>
                    {Object.keys(EngagementVerticals).map((tabKey, index) => {
                        const tabProps = getTabProps(index);
                        const tabObj: IEngagementMetricVertical = EngagementVerticals[tabKey];
                        return (
                            <StyledTab
                                key={tabKey}
                                {...tabProps}
                            >
                                <StyledTabIcon
                                    size="xs"
                                    iconName={tabObj.icon}
                                />
                                <span>{i18nFilter()(tabObj.title)}</span>
                            </StyledTab>
                        );
                    })}
                </TabListStyled>
                {getUtilityButtons()}
            </TabsContainer>
        );
    }, []);

    const chartSectionContent = useMemo(() => {
        if (isLoading) {
            return (
                <PixelPlaceholderLoader
                    width="100%"
                    height="260px"
                />
            );
        }

        if (!data || isEmptyData) {
            return (
                <NoDataGraphContainer>
                    <TableNoData
                        icon="no-data"
                        messageTitle={i18nFilter()("global.nodata.notavilable")}
                    />
                </NoDataGraphContainer>
            );
        }

        return (
            <FlexRow>
                <SingleGraphContainer className={"sharedTooltip"}>
                    <Chart
                        type={"line"}
                        config={getChartConfig({
                            type: "line",
                            height: 280,
                            metric: EngagementVerticals[selectedMetric],
                            filter: isPercentage
                                ? EngagementVerticals[selectedMetric].percentFilter
                                : EngagementVerticals[selectedMetric].filter,
                            data: transformedData,
                            timeGranularity: selectedGranularity.value,
                            yAxisFilter: isPercentage
                                ? [percentageSignFilter]
                                : EngagementVerticals[selectedMetric].yAxisFilter,
                            durationObject,
                            invertTooltipColor: EngagementVerticals[selectedMetric].invertChange,
                        })}
                        data={transformedData}
                    />
                </SingleGraphContainer>
                <SingleGraphLegendContainer>
                    <LegendsTitle>{i18nFilter()("segments.mmx.single.graph.legends.channels.title")}</LegendsTitle>
                    <SegmentChannelsLegends
                        {...{
                            selectedDisplayTypeIndex,
                            id: params.id,
                            data,
                            selectedMetric,
                            selectedGranularity,
                        }}
                    />
                </SingleGraphLegendContainer>
            </FlexRow>
        );
    }, [
        isLoading,
        data,
        isEmptyData,
        selectedMetric,
        isPercentage,
        transformedData,
        selectedGranularity,
        percentageSignFilter,
        durationObject,
        params.id,
        selectedDisplayTypeIndex,
    ]);

    const tabPanels = useMemo(() => {
        return Object.keys(EngagementVerticals).map((tab, idx) => (
            <TabPanel key={`tab-panel-${idx}`}>
                {idx === selectedTab ? (
                    <SectionContainer>
                        <ChartContainer>{chartSectionContent}</ChartContainer>
                    </SectionContainer>
                ) : null}
            </TabPanel>
        ));
    }, [selectedTab, chartSectionContent]);

    const engagementTabsContent = (
        <ContentContainer>
            <Tabs
                onSelect={onTabSelect}
                selectedIndex={selectedTab}
                forceRenderTabPanel={true}
            >
                {tabPanels}
            </Tabs>
        </ContentContainer>
    );

    return (
        <SegmentsSingleGraphLegendsContext.Provider value={{ hiddenLegends, setHiddenLegends }}>
            <BoxContainer ref={chartRef}>
                <StyledTitleContainer>
                    <FlexColumn>
                        <StyledHeaderTitle>
                            <BoxTitle tooltip={i18n("segments.analysis.mmx.single.graph.title.tooltip")}>
                                {i18n("segments.analysis.mmx.single.graph.title")}
                            </BoxTitle>
                        </StyledHeaderTitle>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={subtitleFilters} />
                        </StyledBoxSubtitle>
                    </FlexColumn>
                    <RightFlexRow>
                        <SectionLine>
                            {Object.keys(EngagementVerticals)[selectedTab] === EngagementVerticals.Visits.name ? (
                                <SwitcherContainer>
                                    <Switcher
                                        customClass="CircleSwitcher"
                                        className="rightSwitcher"
                                        selectedIndex={selectedDisplayTypeIndex}
                                        onItemClick={toggleSelectedDisplay}
                                    >
                                        {displayTypeItems.map((displayTypeItem) => (
                                            <CircleSwitcherItem key={displayTypeItem.name}>
                                                {displayTypeItem.display}
                                            </CircleSwitcherItem>
                                        ))}
                                    </Switcher>
                                </SwitcherContainer>
                            ) : null}
                            <SwitcherGranularityContainer
                                itemList={availableGranularities}
                                selectedIndex={selectedGranularity.index}
                                customClass={"CircleSwitcher"}
                                className={"gran-switch"}
                                onItemClick={toggleGranularity}
                            />
                        </SectionLine>
                    </RightFlexRow>
                </StyledTitleContainer>
                {webSource !== devicesTypes.TOTAL ? (
                    <>
                        {tabsContainerContent}
                        {engagementTabsContent}
                    </>
                ) : (
                    <FlexColumnContainer>
                        <RightFlexRow>{getUtilityButtons()}</RightFlexRow>
                        <TotalChartContainer>{chartSectionContent}</TotalChartContainer>
                    </FlexColumnContainer>
                )}
            </BoxContainer>
        </SegmentsSingleGraphLegendsContext.Provider>
    );
};

const transformData = (data, filteredChannelMappings, isPercentage?) => {
    return _.map(filteredChannelMappings, ({ dataKey: chartKey }, index) => {
        const chartData = data?.[chartKey];
        const color = CHART_COLORS.trafficSourcesColorsBySourceMMX[chartKey];
        const baseSeriesConfig: any = {
            name: chartKey,
            color,
            tooltipIndex: index,
        };
        return combineConfigs(
            {
                ...baseSeriesConfig,
                isDataSingleSeries: true,
                data: chartData?.Breakdown?.map((dataPoint: any) => {
                    if (!dataPoint) {
                        return;
                    }
                    const key = dataPoint?.Key;
                    const confidenceLevel = dataPoint?.Value?.Confidence;
                    let value = isPercentage ? dataPoint?.Value?.Percentage : dataPoint.Value?.Value;
                    if (isNaN(parseFloat(value))) {
                        value = null;
                    }

                    return [
                        dateToUTC(key),
                        value,
                        {
                            partial: confidenceLevel >= 0.3 && confidenceLevel < 1,
                            hallowMarker: confidenceLevel >= 0.3 && confidenceLevel < 1,
                            confidenceLevel,
                        },
                    ];
                }),
            },
            [baseSeriesConfig, markerWithDashedConfig],
        );
    });
};
